import { AmcLogo } from "@core/components/logo/amc-logo";
import { SuccessLottieAnimation } from "web-core/components";
import {
  V2Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  VisuallyHidden,
  Select,
  SelectValue,
  SelectContent,
  SelectTrigger,
  SelectItem,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
} from "web-design-system";
import {
  CANCEL_REASONS,
  TRANSACTION_ORDER_TYPE_LABELS,
} from "creso-core/constants";
import {
  Client,
  Plan,
  PlansWithPlanType,
  PlanType,
  SIPPlan,
  STPPlan,
  SWPPlan,
} from "creso-core/types";
import { format } from "date-fns";
import { ArrowDown, ChevronDownIcon } from "lucide-react";
import { useCancelTransaction } from "creso-core/hooks";
import { APIError } from "web-core/utils";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useRouter } from "@tanstack/react-router";
import { getFirstName } from "creso-core/utils";

function getErrorMessage(error: unknown): string {
  if (error instanceof APIError) {
    const { message } = error;
    return message && typeof message === "object"
      ? JSON.stringify(message)
      : (message ?? "Something went wrong");
  }
  if (error instanceof Error) return error.message;
  return "Something went wrong";
}

type Props = PlansWithPlanType & {
  client: Client;
  open: boolean;
  toggle: (show: boolean) => void;
};

type PlanItemProps = {
  plan: Plan;
  planType: PlanType;
};

function PlanName({ plan, planType }: PlanItemProps) {
  if (planType === "XSIP") {
    return (
      <span className="text-sm line-clamp-2">
        {(plan as SIPPlan).scheme_name}
      </span>
    );
  }
  if (planType === "STP") {
    return (
      <div className="flex flex-col">
        <span className="text-sm mb-2 line-clamp-1">
          {(plan as STPPlan).switch_out_scheme_name}
        </span>
        <ArrowDown />
        <span className="text-sm mt-2 line-clamp-1">
          {(plan as STPPlan).switch_in_scheme_name}
        </span>
      </div>
    );
  }
  if (planType === "SWP") {
    return (
      <span className="text-sm mt-2 line-clamp-2">
        {(plan as SWPPlan).scheme_name}
      </span>
    );
  }
  return null;
}

function PlanStats({ plan }: PlanItemProps) {
  return (
    <>
      <div className="flex flex-col gap-1 flex-1">
        <span className="text-xs text-muted-foreground">Amount</span>
        <span className="text-sm font-semibold">{plan.installment_amount}</span>
      </div>
      <div className="flex flex-col gap-1 flex-1">
        <span className="text-xs text-muted-foreground">Next installment</span>
        <span className="text-sm font-semibold">
          {format(new Date(plan.installment_due_date as string), "dd MMM yyyy")}
        </span>
      </div>
    </>
  );
}

export function CancelOrderConfirmationDialog(props: Props) {
  const { mutateAsync: cancelOrder } = useCancelTransaction();

  const { plans, planType, client } = props;

  const [reason, setReason] = useState<string | null>(null);
  const [pendingPlans, setPendingPlans] = useState<Plan[]>(plans as Plan[]);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (props.open) {
      setPendingPlans(plans as Plan[]);
      setErrors({});
      setIsSuccess(false);
    }
    return () => {
      setReason(null);
    };
  }, [props.open, plans]);

  const router = useRouter();

  const handleCancel = async () => {
    if (!reason) {
      toast.error("Please select a reason for cancelling");
      return;
    }

    const total = pendingPlans.length;

    const results = await Promise.allSettled(
      pendingPlans.map((plan) =>
        cancelOrder({
          plan_id: plan.id,
          plan_type: planType,
          investment_account_guid: client.investment_account ?? "",
          user_remarks: reason,
          user_remarks_optional: reason === "13" ? "Cancelled via Creso" : "",
          initiated_via: "web",
        })
      )
    );

    const failedPlans: Plan[] = [];
    const nextErrors: Record<string, string> = {};
    results.forEach((result, index) => {
      if (result.status === "rejected") {
        const plan = pendingPlans[index];
        failedPlans.push(plan);
        nextErrors[plan.id.toString()] = getErrorMessage(result.reason);
      }
    });

    if (failedPlans.length === 0) {
      setIsSuccess(true);
      return;
    }

    // Stay on the dialog and keep only the plans that failed.
    setPendingPlans(failedPlans);
    setErrors(nextErrors);
    toast.error(
      `Failed to cancel ${failedPlans.length} of ${total} ${TRANSACTION_ORDER_TYPE_LABELS[planType]}${total > 1 ? "s" : ""}`
    );
  };

  const openCart = () => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: "transaction",
        action: "view",
        client_id: client.id,
      }),
    });
    props.toggle(false);
  };

  return (
    <V2Dialog open={props.open} onOpenChange={props.toggle}>
      <DialogContent className="p-6 gap-6 space-y-0 w-120 max-w-none">
        {isSuccess ? (
          <>
            <VisuallyHidden>
              <DialogTitle>Added to Cart</DialogTitle>
            </VisuallyHidden>
            <div className="flex flex-col items-center gap-2 py-4">
              <SuccessLottieAnimation loop={false} height={150} width={150} />
              <span className="text-lg font-semibold">Added to Cart</span>
              <span className="text-sm text-muted-foreground text-center">
                {getFirstName(client.name)} needs to authorise SIP cancellation
                from their device
              </span>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => props.toggle(false)}>
                Close
              </Button>
              <Button onClick={openCart}>Open Cart</Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <VisuallyHidden>
                <DialogTitle className="text-lg">
                  Confirm {TRANSACTION_ORDER_TYPE_LABELS[planType]}{" "}
                  Cancellation?
                </DialogTitle>
              </VisuallyHidden>
              <div className="flex flex-col gap-4">
                <div className="text-lg font-semibold">
                  Confirm {TRANSACTION_ORDER_TYPE_LABELS[planType]} Cancellation
                </div>
                <div className="bg-border h-px w-full" />
                <div className="flex flex-col px-4 bg-card rounded-lg divide-y divide-border max-h-80 overflow-y-auto">
                  {pendingPlans.map((plan) => {
                    const error = errors[plan.id.toString()];
                    return (
                      <Collapsible
                        key={plan.id}
                        defaultOpen={pendingPlans.length === 1}
                        className="group/collapsible py-4 flex flex-col"
                      >
                        <CollapsibleTrigger className="flex flex-row min-h-10 items-center gap-3 justify-between w-full text-left">
                          <div className="flex flex-row gap-3 items-center">
                            <AmcLogo
                              size="sm"
                              amc_code={plan.amc_morningstar_code}
                            />
                            <PlanName plan={plan} planType={planType} />
                          </div>
                          <ChevronDownIcon className="size-4 shrink-0 transition-transform group-data-[state=open]/collapsible:rotate-180" />
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <div className="flex flex-row w-full mt-5">
                            <PlanStats plan={plan} planType={planType} />
                          </div>
                        </CollapsibleContent>
                        {error && (
                          <span className="text-xs text-destructive mt-2">
                            {error}
                          </span>
                        )}
                      </Collapsible>
                    );
                  })}
                </div>
                <Select onValueChange={(value) => setReason(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select reason for cancelling" />
                  </SelectTrigger>
                  <SelectContent>
                    {CANCEL_REASONS.map((reason) => (
                      <SelectItem key={reason.value} value={reason.value}>
                        {reason.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">
                  Once added to cart, notify the client to authorise from their
                  device.
                </p>
              </div>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => props.toggle(false)}>
                No
              </Button>
              <Button onClick={handleCancel}>Proceed</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </V2Dialog>
  );
}
